import React, { useState } from "react";
import { ClipboardCopy, Download, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { http, api } from "@/api";
import { buildDebugReport, getDiagEvents, getAuthDiag, logDiag, getTraced, explainOccurrence } from "@/lib/diagnostics";

// Temporary READ-ONLY diagnostics. Only GETs; never mutates tasks or state.
export const DiagnosticsPanel = ({ tasks, visible, counts, view, openTask, user }) => {
    const [busy, setBusy] = useState(false);
    const [last, setLast] = useState(null);
    const [traceId, setTraceId] = useState("");
    const [traceIso, setTraceIso] = useState("");
    const [trace, setTrace] = useState(null);
    const traced = getTraced().slice().reverse();

    /** TEMPORARY: end-to-end trace of ONE task_id + occurrenceIso — frontend render decision +
     *  backend persisted state + task_events (before/after of every completion write) + ring buffer. */
    const runTrace = async (id = traceId.trim(), iso = traceIso.trim()) => {
        if (!id) return;
        setBusy(true);
        try {
            const local = tasks.find((t) => t.id === id) || null;
            const explain = explainOccurrence(local, iso || local?.date || null);
            let backend = null; let backendError = null;
            try { backend = await api.taskTrace(id, iso || undefined); } catch (err) { backendError = `${err?.response?.status || "network"} ${String(err?.response?.data?.detail || err?.message || "").slice(0, 120)}`; }
            const ring = getDiagEvents().filter((e) => e.task_id === id);
            const L = ["=== OCCURRENCE TRACE ===", `task_id=${id} occurrenceIso=${iso || "(none)"} generated_at=${new Date().toISOString()} TODAY=${new Date().toDateString()}`,
                "", "--- 6. FRONTEND RENDER DECISION (state currently held by the app) ---", JSON.stringify(explain, null, 1),
                "", "--- 3./5. BACKEND PERSISTED STATE + VERDICT (GET /tasks/{id}/trace) ---", backendError ? `FAILED: ${backendError}` : JSON.stringify({ task: backend.task, verdict: backend.verdict, server_now_ms: backend.server_now_ms }, null, 1),
                "", `--- 3. BACKEND task_events (${backend?.events?.length ?? 0}, newest first: action · by · trace · BEFORE → AFTER) ---`];
            for (const e of backend?.events || []) L.push(`${new Date(e.ts).toISOString()} ${e.action} iso=${e.source_iso} by=${e.by} trace=${e.trace_id || "-"} ${e.patch_keys ? `keys=${JSON.stringify(e.patch_keys)}` : ""}
   before=${JSON.stringify(e.before)}
   after =${JSON.stringify(e.after)}`);
            L.push("", `--- 1./2./4. FRONTEND RING BUFFER for this task (${ring.length}) ---`);
            for (const e of ring) L.push(`${e.ts} ${e.kind} ${e.method || ""} ${e.url || ""} ${e.status ?? ""} ${e.iso ? `iso=${e.iso}` : ""} ${e.source_iso ? `source=${e.source_iso}` : ""} ${e.view ? `view=${e.view}` : ""} ${e.trace_id ? `trace=${e.trace_id}` : ""} ${e.message || ""} ${e.state ? JSON.stringify(e.state) : ""}`.replace(/\s+/g, " ").trim());
            L.push("=== END TRACE ===");
            const text = L.join("\n");
            setTrace(text);
            try { await navigator.clipboard.writeText(text); toast.success("Occurrence trace copied"); } catch { toast.success("Trace ready below"); }
            logDiag({ kind: "diag", task_id: id, iso: iso || null, message: "occurrence trace built" });
        } finally { setBusy(false); }
    };

    const gather = async () => {
        let diagnose = null; let diagnoseError = null;
        try {
            const q = openTask?.text?.split(/\s+/).slice(0, 2).join(" ") || "";
            const r = await http.get("/tasks/diagnose", { params: { q, tz_offset_min: new Date().getTimezoneOffset() } });
            diagnose = r.data;
        } catch (err) {
            diagnoseError = `${err?.response?.status || "network"} ${String(err?.response?.data?.detail || err?.message || "").slice(0, 120)}`;
        }
        const text = buildDebugReport({ tasks, view, counts, visible, openTask, diagnose, diagnoseError, user });
        return { text, json: { generated_at: new Date().toISOString(), view, counts, auth: getAuthDiag(), open_task: openTask, visible, diagnose, diagnose_error: diagnoseError, events: getDiagEvents() } };
    };

    const copy = async () => {
        setBusy(true);
        try {
            const rep = await gather();
            setLast(rep);
            try { await navigator.clipboard.writeText(rep.text); }
            catch { const ta = document.createElement("textarea"); ta.value = rep.text; document.body.appendChild(ta); ta.select(); document.execCommand("copy"); ta.remove(); }
            logDiag({ kind: "diag", message: "debug report copied" });
            toast.success("Debug report copied — paste it into ChatGPT");
        } catch (err) {
            toast.error(`Could not build report: ${err?.message || err}`);
        } finally { setBusy(false); }
    };

    const download = async () => {
        const rep = last || (await gather());
        const blob = new Blob([JSON.stringify(rep.json, null, 2)], { type: "application/json" });
        const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `meyetasks-debug-${Date.now()}.json`; a.click(); URL.revokeObjectURL(a.href);
    };

    return (
        <div className="space-y-4 p-4" data-testid="diagnostics-panel">
            <div className="flex items-start gap-2 text-[11px] text-zinc-500 font-mono">
                <ShieldCheck size={12} className="mt-0.5 flex-shrink-0 text-meye-cyan" />
                <span>Read-only. Gathers build, clock, sanitized auth status, visible recurring obligations, the backend diagnose view and the last {getDiagEvents().length} events. No tokens, cookies or headers are ever included.</span>
            </div>
            <button type="button" onClick={copy} disabled={busy} data-testid="copy-debug-report-btn"
                className="w-full flex items-center justify-center gap-2 bg-meye-fuchsia text-black font-brand uppercase tracking-widest text-sm px-4 py-3 rounded disabled:opacity-50">
                {busy ? <Loader2 size={16} className="animate-spin" /> : <ClipboardCopy size={16} />} COPY DEBUG REPORT
            </button>
            <button type="button" onClick={download} disabled={busy} data-testid="download-debug-json-btn"
                className="w-full flex items-center justify-center gap-2 border border-meye-border text-zinc-300 font-mono text-xs px-4 py-2 rounded hover:border-meye-cyan/60">
                <Download size={14} /> Download JSON
            </button>
            {last && <pre className="text-[10px] text-zinc-400 font-mono whitespace-pre-wrap max-h-64 overflow-y-auto border border-meye-border rounded p-2 bg-black/30" data-testid="debug-report-preview">{last.text.slice(0, 4000)}{last.text.length > 4000 ? "\n…" : ""}</pre>}

            <div className="border-t border-meye-border pt-4 space-y-2" data-testid="occurrence-trace">
                <div className="text-[10px] uppercase tracking-widest text-meye-cyan font-brand">trace one occurrence (task_id + occurrenceIso)</div>
                <div className="flex gap-2">
                    <input value={traceId} onChange={(e) => setTraceId(e.target.value)} placeholder="task id" className="flex-1 min-w-0 bg-black/30 border border-meye-border rounded px-2 py-1.5 text-xs font-mono text-zinc-200" data-testid="trace-task-id" />
                    <input value={traceIso} onChange={(e) => setTraceIso(e.target.value)} placeholder="YYYY-MM-DD" className="w-28 bg-black/30 border border-meye-border rounded px-2 py-1.5 text-xs font-mono text-zinc-200" data-testid="trace-iso" />
                    <button type="button" onClick={() => runTrace()} disabled={busy || !traceId.trim()} className="px-3 py-1.5 rounded bg-meye-cyan text-black text-xs font-brand uppercase tracking-widest disabled:opacity-40" data-testid="trace-run-btn">Trace</button>
                </div>
                {traced.length > 0 && (
                    <div className="space-y-1" data-testid="traced-list">
                        <div className="text-[10px] text-zinc-500 font-mono">recently completed on this device — tap to trace</div>
                        {traced.slice(0, 12).map((tr) => (
                            <button key={`${tr.task_id}:${tr.iso}`} type="button" onClick={() => { setTraceId(tr.task_id); setTraceIso(tr.iso || ""); runTrace(tr.task_id, tr.iso || ""); }}
                                className="w-full text-left text-[11px] font-mono text-zinc-300 border border-meye-border rounded px-2 py-1 hover:border-meye-cyan/60 truncate" data-testid={`traced-${tr.task_id}-${tr.iso || "none"}`}>
                                {new Date(tr.ts).toLocaleString()} · {tr.task_id} · {tr.iso || "one-shot"} · "{tr.title}"
                            </button>
                        ))}
                    </div>
                )}
                {trace && <pre className="text-[10px] text-zinc-400 font-mono whitespace-pre-wrap max-h-72 overflow-y-auto border border-meye-border rounded p-2 bg-black/30" data-testid="occurrence-trace-output">{trace.slice(0, 6000)}{trace.length > 6000 ? "\n…" : ""}</pre>}
            </div>
        </div>
    );
};

export default DiagnosticsPanel;
